import { Component, Input, OnInit, ElementRef, OnChanges, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { NavRoute } from '../nav.route.interface';
import { NavigationService, NavDividerCharacter } from '../navigation.service';
import { SecurityService } from 'app/core/security/security.service';
import { UserJsVm } from '../../generated/Administration/Models/UserJsVm';
import { CheckNetworkService } from 'local-db/services/check-network.service';

/**
 * This component is intended to be used in the top header bar
 * to create a nested navigation. Dropdowns are opened by
 * click.
 */
@Component({
    selector: 'pcg-top-click-nav',
    templateUrl: './top-click-nav.component.html',
    styleUrls: ['./top-click-nav.component.scss'],
    standalone: false
})

export class TopClickNavComponent implements OnInit, OnChanges {

	@Input() navRoutes: NavRoute[] = [];
	@Input() menuItemId = 'root';
	@Input() isRoot = true;

	user: UserJsVm;
	isOpen: boolean;
	currNavMenuItem: string;
	currSelectedNavMenuItem: string;
	currOpenSecondaryNavMenu: string;
	currForceSelect: string;
	ulClasses: string[];
	loopNavRoutes: NavRoute[] = [];
	userNavOpen = false;

	constructor(
		public navService: NavigationService
		, public elRef: ElementRef
		, public sec: SecurityService
		, private router: Router
		, private modalService: NgbModal
	) { }

	ngOnInit() {
		// Get the current user information
		this.sec.user$.subscribe(user => { this.user = user; });
		// Triggred on update of currently OPEN menu item
		this.navService.currOpenPrimaryNavMenu$.subscribe(currNavMenuItem => {
			this.currNavMenuItem = currNavMenuItem;
			this.isOpen = currNavMenuItem !== '' && currNavMenuItem.startsWith(this.menuItemId);
			this.userNavOpen = currNavMenuItem === ['root', 'user'].join(NavDividerCharacter);
			// The first ul should have classes indicating it is the root.
			// Nested ul elements, those at higher depths, are dropdown menus
			// and need classes indicating that.
			this.ulClasses = this.isRoot 
				? ['nav', 'navbar-nav', 'top-nav-bg', this.isOpen ? 'nav-open' : ''] 
				: ['dropdown-menu', 'top-nav-bg', this.isOpen ? 'nav-open' : ''];
		});
		// Triggered on update of currently SELECTED menu item
		this.navService.currSelectedMainNavMenu$.subscribe(currSelectedNavMenu => {
			this.currSelectedNavMenuItem = currSelectedNavMenu ? currSelectedNavMenu.id : 'root';
		});
		this.navService.currOpenSecondaryNavMenu$.subscribe(currSelectedNavMenu => {
			this.currOpenSecondaryNavMenu = currSelectedNavMenu ? currSelectedNavMenu.id : 'root';
		});
		this.navService.currForceSelect$.subscribe(currForceSelect => {
			this.currForceSelect = currForceSelect;
		});
	}

	ngOnChanges(changes: SimpleChanges) {
		const chng = changes['navRoutes'];
		const cur = chng.currentValue;
		const prev = chng.previousValue;
		if (cur !== prev) { this.loopNavRoutes = this.getNavRoutes(); }
	}

	// Show or hide submenu based on dropdown click
	clickDropdown(childMenuId: string) {
		if (childMenuId === 'user') { childMenuId = ['root', 'user'].join(NavDividerCharacter); }

		// If the submenu is open, close it
		if (this.currNavMenuItem.startsWith(childMenuId)) { this.navService.setCurrOpenNavMenu(this.menuItemId); }
		// Otherwise, open the submenu
		else { this.navService.setCurrOpenNavMenu(childMenuId); }
		return false;
	}

	clickLink() { this.navService.setCurrOpenNavMenu(''); }

	// Logout removes all of our security variables
	// and redirects user to root
	logout() {
		const isOnline = CheckNetworkService.checkNetworkStatus();
		if (
			isOnline
			|| (
				!isOnline
				&& confirm("Are you sure you want log out? You are offline and may not be able to log in until you are back online.")
			)
		) {
			this.sec.setSecurity(null, null, null);
			this.deleteAllCookies();
			this.router.navigate(['/']);
		}
	}

	// This is used above to delete all cookies on logout
	deleteAllCookies() {
		const cookies = document.cookie.split(';');

		for (let i = 0; i < cookies.length; i++) {
			const cookie = cookies[i];
			const eqPos = cookie.indexOf('=');
			const name = eqPos > -1 
				? cookie.substr(0, eqPos) 
				: cookie;
			document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT';
		}
	}

	getNavRoutes() {
		const routeIndex = this.navService.getCurrRouteIndex(this.navRoutes);
		return this.navRoutes?.map((o, i) => {
			return { ...o, index: i };
		}).filter(route => !route?.shouldDisplay || route?.shouldDisplay(routeIndex)) ?? [];
	}
}
